import { JSX, useState, useEffect, useContext, useMemo } from "react";
import { PieChart, Pie, PieSectorShapeProps, ResponsiveContainer, Sector, Legend, Tooltip } from "recharts";
import { QesColors } from "@app/shared/qes-standards/colors";
import { NetworkIntegrityChangeArrow } from "@app/projects/network-assessment/network-integrity/widgets/NetworkIntegrityChangeArrow";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityJobStatus } from "@app/projects/enumerators";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import { NetworkIntegrityDisplayMode } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { NetworkProviderBreakdownDescription } from "@app/projects/network-assessment/network-integrity/widgets/descriptions/NetworkProviderBreakdownDescription";
import { NetworkIntegrityWidgetInfoFlyout } from "@app/projects/network-assessment/network-integrity/widgets/NetworkIntegrityWidgetInfoFlyout";
import { RoundedLegendIcon } from "@app/projects/network-assessment/network-integrity/widgets/RoundedLegendIcon";
import { WidgetLoading, WidgetHeader, Widget, WidgetBody } from "@app/projects/network-assessment/network-integrity/widgets/Widget";
import { WidgetMenu } from "@app/projects/network-assessment/network-integrity/widgets/WidgetMenu";

interface BreakdownSegment {
  afterTermed: number;
  color: string;
  current: number;
  name: string;
  testId: string;
}

function NetworkProviderBreakdownWidget(): JSX.Element {
  const title = "Provider Breakdown";

  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const project = networkIntegrityContext.project!;
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const showTotalAfterTermed = networkIntegrityContext.displayMode === NetworkIntegrityDisplayMode.TotalAfterTermed;

  const [loading, setLoading] = useState<boolean>(false);
  const [totalProviderCount, setTotalProviderCount] = useState<number>(0);
  const [providerCountWithAlerts, setProviderCountWithAlerts] = useState<number>(0);
  const [providerCountWithOnlyWarnings, setProviderCountWithOnlyWarnings] = useState<number>(0);
  const [providerCountWithNoAlertsOrWarning, setProviderCountWithNoAlertsOrWarning] = useState<number>(0);
  const [afterTermedProviderCountWithAlerts, setAfterTermedProviderCountWithAlerts] = useState<number>(0);
  const [afterTermedProviderCountWithOnlyWarnings, setAfterTermedProviderCountWithOnlyWarnings] = useState<number>(0);
  const [afterTermedProviderCountWithNoAlertsOrWarnings, setAfterTermedProviderCountWithNoAlertsOrWarnings] = useState<number>(0);
  const [percentOfProvidersWithIssues, setPercentOfProvidersWithIssues] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setTotalProviderCount(0);
    setProviderCountWithAlerts(0);
    setProviderCountWithOnlyWarnings(0);
    setProviderCountWithNoAlertsOrWarning(0);
    setPercentOfProvidersWithIssues(0);
    setAfterTermedProviderCountWithAlerts(0);
    setAfterTermedProviderCountWithOnlyWarnings(0);
    setAfterTermedProviderCountWithNoAlertsOrWarnings(0);
    async function fetchProviderBreakdownResults() {
      try {

        if (networkIntegrityContext.projectRunConfigurationId) {
          const {
            totalProviderCount, providerCountWithAlerts, providerCountWithOnlyWarnings, providerCountWithNoAlertsOrWarnings,
            afterTermedProviderCountWithAlerts, afterTermedProviderCountWithOnlyWarnings, afterTermedProviderCountWithNoAlertsOrWarnings
          } =
            await networkIntegrityService.getProvidersBreakdown(project.projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes);

          if (!cancelled) {
            setTotalProviderCount(totalProviderCount);
            setProviderCountWithAlerts(providerCountWithAlerts);
            setProviderCountWithOnlyWarnings(providerCountWithOnlyWarnings);
            setProviderCountWithNoAlertsOrWarning(providerCountWithNoAlertsOrWarnings);
            setPercentOfProvidersWithIssues(((providerCountWithAlerts + providerCountWithOnlyWarnings) / totalProviderCount) * 100);
            setAfterTermedProviderCountWithAlerts(afterTermedProviderCountWithAlerts ?? 0);
            setAfterTermedProviderCountWithOnlyWarnings(afterTermedProviderCountWithOnlyWarnings ?? 0);
            setAfterTermedProviderCountWithNoAlertsOrWarnings(afterTermedProviderCountWithNoAlertsOrWarnings ?? 0);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (networkIntegrityContext.isJobStatusLoading === false && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished) {
      setLoading(true);
      fetchProviderBreakdownResults();
    }

    return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes]);

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading additionalClassNames="ni-provider-breakdown-widget"><WidgetHeader title={title} /></WidgetLoading>);

  const afterTermedTotal = afterTermedProviderCountWithAlerts + afterTermedProviderCountWithOnlyWarnings + afterTermedProviderCountWithNoAlertsOrWarnings;
  const afterTermedIssuesPct = afterTermedTotal
    ? ((afterTermedProviderCountWithAlerts + afterTermedProviderCountWithOnlyWarnings) / afterTermedTotal) * 100
    : 0;

  const currentIssuesPctRounded = Math.round(percentOfProvidersWithIssues);
  const afterTermedIssuesPctRounded = Math.round(afterTermedIssuesPct);
  const ppChange = afterTermedIssuesPctRounded - currentIssuesPctRounded;
  const displayTotal = showTotalAfterTermed ? afterTermedTotal : totalProviderCount;

  const red = QesColors.QesTertiaryRed2;
  const yellow = QesColors.QesTertiaryYellow2;
  const blue = QesColors.QesTertiaryBlue1;
  const chartGray = QesColors.QesPrimaryGray4;
  const textGray = QesColors.QesPrimaryGray5;

  const segments: BreakdownSegment[] = [
    { afterTermed: afterTermedProviderCountWithAlerts, color: red, current: providerCountWithAlerts, name: "Providers With Alerts", testId: "provider-breakdown-alerts-decrease" },
    { afterTermed: afterTermedProviderCountWithOnlyWarnings, color: yellow, current: providerCountWithOnlyWarnings, name: "Providers With Warnings Only", testId: "provider-breakdown-warnings-decrease" },
    { afterTermed: afterTermedProviderCountWithNoAlertsOrWarnings, color: blue, current: providerCountWithNoAlertsOrWarning, name: "Providers With No Alerts or Warnings", testId: "provider-breakdown-no-alerts-decrease" }
  ];

  const legendData = segments.map((segment) => ({
    color: segment.color,
    name: segment.name,
    value: showTotalAfterTermed ? segment.afterTermed : segment.current
  }));

  const pieData = displayTotal === 0 ? [{ color: chartGray, name: "No Data", value: 1 }] : legendData.filter((d) => d.value > 0);

  const hasDecrease = (segment: BreakdownSegment): boolean =>
    showTotalAfterTermed && segment.afterTermed != null && segment.afterTermed < segment.current;

  const renderDecreaseIndicator = (segment: BreakdownSegment): JSX.Element | null => {
    if (!hasDecrease(segment)) return null;
    const decrease = segment.current - segment.afterTermed;
    return (
      <span className="ni-provider-breakdown-widget__decrease-indicator" data-testid={segment.testId}>
        <NetworkIntegrityChangeArrow direction="decrease" />
        ({decrease.toLocaleString()})
      </span>
    );
  };

  const renderLegendText = (value: string, segment: BreakdownSegment): JSX.Element => {
    return (
      <span className="ni-provider-breakdown-widget__legend-text">
        {value}
        <span className="ni-provider-breakdown-widget__legend-count">
          {showTotalAfterTermed && segment ? (
            <>
              {segment.afterTermed.toLocaleString()}
              {renderDecreaseIndicator(segment)}
            </>
          ) : segment.current.toLocaleString()}
        </span>
      </span>
    );
  };

  const renderBreakdownLegend = () => (
    <div>
      {segments.map((segment) => (
        <div key={segment.name} className="d-flex flex-row align-items-start">
          <svg width={13} height={13} viewBox="0 0 32 32" style={{ display: "inline-block", flexShrink: 0, marginRight: 6, marginTop: 4 }}>
            <RoundedLegendIcon fill={segment.color} />
          </svg>
          {renderLegendText(segment.name, segment)}
        </div>
      ))}
    </div>
  );

  const renderTooltip = () => {
    return (
      <div className="ni-trend-tooltip">
        <div className="ni-trend-tooltip__header">
          <div className="ni-trend-tooltip__header-type">Alert Type</div>
          <div className="ni-trend-tooltip__header-count">Alert Count</div>
        </div>

      {legendData.map((data) => (
        <div key={data.name} className="ni-trend-tooltip__row">
          <span className="ni-trend-tooltip__color" style={{ background: data.color }}></span>
          <span className="ni-trend-tooltip__label">{data.name}</span>
          <span className="ni-trend-tooltip__value">{data.value.toLocaleString()}</span>
        </div>
      ))}
      </div>
    );
  }

  return (
    <Widget additionalClassNames="ni-provider-breakdown-widget">
      <WidgetHeader title={title}>
        <WidgetMenu>
          <NetworkIntegrityWidgetInfoFlyout title="Network Provider Breakdown" description={<NetworkProviderBreakdownDescription/>}/>
        </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
        <div className="d-flex flex-row ni-provider-breakdown-widget__chart-container">
          <div className="d-flex flex-column w-50">
            <ResponsiveContainer height="100%" width="100%">
              <PieChart>
                <Legend
                  // recharts says align is deprecated, but position doesn't have the same effect
                  align="left"
                  layout="vertical"
                  wrapperStyle={{ left: 0, top: 50 }}
                  content={renderBreakdownLegend()}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="d-flex flex-column w-50">
            <ResponsiveContainer height="100%" width="100%">
              <PieChart>
                <text
                  x="50%"
                  y="48%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ fill: textGray, fontFamily: "inherit" }}
                >
                  {showTotalAfterTermed ? (
                    <>
                      <tspan className="ni-provider-breakdown-widget__pct" x="50%" dy="-20" fontWeight="bold" fontSize="1rem">
                        {displayTotal === 0 ? "---" : `${afterTermedIssuesPctRounded}%`}
                      </tspan>
                      <tspan className="ni-provider-breakdown-widget__pct-change" x="50%" dy="13" fontWeight="bold" fontSize="0.75rem" fill={ppChange > 0 ? QesColors.QesPrimaryRed : ppChange < 0 ? QesColors.QesPrimaryGreen : textGray}>
                        {displayTotal === 0 ? "" : `${ppChange > 0 ? "↑ " : ppChange < 0 ? "↓ " : ""}${Math.abs(ppChange)} pt`}
                      </tspan>
                    </>
                  ) : (
                    <tspan className="ni-provider-breakdown-widget__pct" x="50%" dy="-13" fontWeight="bold" fontSize="1rem">
                      {totalProviderCount === 0 ? "---" : `${percentOfProvidersWithIssues?.toFixed(0)}%`}
                    </tspan>
                  )}
                  <tspan x="50%" dy="13" fontWeight="bold" fontSize="0.6rem">of Providers</tspan>
                  <tspan x="50%" dy="13" fontWeight="bold" fontSize="0.6rem">have Integrity</tspan>
                  <tspan x="50%" dy="13" fontWeight="bold" fontSize="0.6rem">Issues</tspan>
                </text>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="70%"
                  outerRadius="100%"
                  cornerRadius="10%"
                  paddingAngle={2}
                  minAngle={10}
                  labelLine={false}
                  fill={textGray}
                  shape={(shapeProps: PieSectorShapeProps) => <Sector {...shapeProps} fill={shapeProps.payload.color} />}
                />
                {displayTotal !== 0 && <Tooltip content={renderTooltip} position={{x:-100, y:0}} cursor={false}/>}
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </WidgetBody>
    </Widget>
  );
}

export { NetworkProviderBreakdownWidget };